import dayjs, { Dayjs } from 'dayjs';
import { useStoreActions } from 'easy-peasy';
import { Formik } from 'formik';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import ClearIcon from '@mui/icons-material/Clear';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Grid2 as Grid } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { BaseResponse } from '../../api/types';
import PageLoader from '../../components/pageloader/pageLoader';
import ExportButton from '../../features/Policy/components/ExportButton';
import { useDownloadPolicy } from '../../features/Policy/hooks';
import {
  PolicyEntity,
  PolicyProgramEntity,
  SearchPoliciesQuery,
  SearchPoliciesResponse,
} from '../../features/Policy/types';

const validationSchema = Yup.object({
  service_date: Yup.mixed()
    .nullable()
    .transform((value) => (dayjs.isDayjs(value) ? value.toDate() : value))
    .required('Policy date is required'),
});

type Props = {
  orgId: string;
  onFormSubmit: (value: boolean) => void;
  onDataChange: (data: PolicyEntity[]) => void;
  onShowLOB: (value: boolean) => void;
  showLOB: boolean;
  urlParams: any;
};

interface FormValues {
  keywords: string;
  lob: string;
  policy_shortname: string;
  procedure_code: string;
  service_date: Dayjs;
  status: string;
}

const SearchForm = ({
  onDataChange,
  onFormSubmit,
  onShowLOB,
  orgId,
  showLOB,
  urlParams,
}: Props) => {
  const formikRef = useRef<HTMLFormElement | null>(null);
  const { downloadFile } = useDownloadPolicy();

  const [openLoader, setOpenLoader] = useState(false);
  const [lobOptions] = useState(
    urlParams?.lob ? urlParams?.lob?.split(',') : []
  );
  const [isExportLoading, setIsExportLoading] = useState(false);

  const navigate = useNavigate();

  const [selectedLobs, setSelectedLobs] = useState(lobOptions);
  const [programPolicies, setProgramPolicies] = useState<PolicyProgramEntity[]>(
    []
  );

  const showPrograms = programPolicies.length > 0;

  const policyAction = useStoreActions((actions: any) => actions.policy);

  const serviceDateFromUrl = dayjs(urlParams?.service_date, 'YYYY-MM-DD', true);

  const initialValues = useMemo(() => {
    const allowedStatuses = ['Published', 'Review'];
    const today = dayjs();

    return {
      keywords: urlParams?.keyword,
      lob: urlParams?.lob ? urlParams?.lob?.split(',') : [],
      policy_shortname: urlParams?.policy_shortname
        ? urlParams?.policy_shortname
        : null,
      procedure_code: urlParams?.procedure_code,
      service_date: serviceDateFromUrl.isValid() ? serviceDateFromUrl : today,
      status: urlParams?.status
        ? Array.isArray(urlParams.status)
          ? urlParams.status.filter((s: string) => allowedStatuses.includes(s))
          : allowedStatuses.includes(urlParams.status)
            ? [urlParams.status]
            : ['Published']
        : ['Published'],
    };
  }, [urlParams, serviceDateFromUrl]);

  const buildRequestParams = useCallback(
    (values: FormValues) =>
      ({
        keyword: values?.keywords,
        //lob: values?.lob,
        lob: selectedLobs,
        orgId: orgId,
        //policy_shortname: (values?.policy_shortname && values?.policy_shortname!==-1) ? values?.policy_shortname : ""
        policy_shortname: values?.policy_shortname
          ? values?.policy_shortname
          : '',
        procedure_code: values?.procedure_code,
        service_date: values?.service_date
          ? moment(values?.service_date?.toDate?.()).format('YYYY-MM-DD')
          : null,
        // policy_type: values?.policy_type,
        status: values?.status,
      }) as SearchPoliciesQuery,
    [orgId, selectedLobs]
  );

  const buildQueryString = useCallback(
    (values: FormValues) => {
      const formattedDate = moment(values?.service_date?.toDate?.()).format(
        'YYYY-MM-DD'
      );

      const params = new URLSearchParams({
        keyword: values?.keywords || '',
        lob: selectedLobs || '',
        policy_shortname: values?.policy_shortname || '',
        procedure_code: values?.procedure_code || '',
        service_date: formattedDate,
        status: values?.status || '',
      });
      if (urlParams?.access_token) {
        params.set('access_token', urlParams.access_token);
      }
      return params.toString();
    },
    [selectedLobs, urlParams.access_token]
  );

  const handleFormSubmit = useCallback(
    async (values: FormValues) => {
      onFormSubmit(true);
      setOpenLoader(true);

      const params = buildRequestParams(values);
      const query = buildQueryString(values);
      navigate(`?${query}`, { replace: true });

      try {
        const result = (await policyAction.searchPolicies(
          params
        )) as BaseResponse<SearchPoliciesResponse>;
        if (result.success && result.data) {
          const data = result.data;
          onDataChange(data?.results);
          if (data?.programPolicies) {
            const activeShortnames = new Set(
              (data.results || [])
                .map((r) => r.policy_shortname)
                .filter((s) => s != null && s !== '')
            );
            const activePrograms = data.programPolicies.filter((p) =>
              activeShortnames.has(p.shortname)
            );
            setProgramPolicies(activePrograms);
          }
        }
      } catch {
        onDataChange([]);
      } finally {
        setOpenLoader(false);
      }
    },
    [
      buildQueryString,
      buildRequestParams,
      navigate,
      onDataChange,
      onFormSubmit,
      policyAction,
    ]
  );

  const handleExport = useCallback(
    async (values: FormValues) => {
      setIsExportLoading(true);
      const params = buildRequestParams(values);
      const query = buildQueryString(values);
      navigate(`?${query}`, { replace: true });
      try {
        await downloadFile(params.orgId);
      } finally {
        setIsExportLoading(false);
      }
    },
    [buildQueryString, buildRequestParams, downloadFile, navigate]
  );

  useEffect(() => {
    setSelectedLobs(lobOptions);

    if (lobOptions && lobOptions?.length > 1) {
      onShowLOB(true);
    } else {
      onShowLOB(false);
    }
  }, [lobOptions, onShowLOB]);

  useEffect(() => {
    handleFormSubmit(initialValues);
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        component="section"
        className="headerBox"
        style={{ marginTop: '10px' }}
      >
        <Formik
          initialValues={initialValues}
          ref={formikRef} // Attach Formik instance to ref
          onSubmit={handleFormSubmit}
          validationSchema={validationSchema}
          enableReinitialize={true}
        >
          {(formikProps) => {
            const {
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isValid,
              setFieldTouched,
              setFieldValue,
              touched,
              values,
            } = formikProps;

            const today = dayjs();
            const maxDate = today.add(45, 'day');

            const isFinalChecked = values.status.includes('Published');
            const isDraftChecked = values.status.includes('Review');

            const minDate =
              isDraftChecked && !isFinalChecked ? today : undefined; // undefined means no restriction

            return (
              <form onSubmit={handleSubmit} noValidate>
                {/** Main container */}
                <Grid container className="container" spacing={2}>
                  <Grid
                    size={{
                      md: showPrograms ? 7 : 12,
                      sm: showPrograms ? 7 : 12,
                      xs: 12,
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="h6"
                      className="filterFormLabel"
                      style={{ fontSize: '14px', fontWeight: 'bold' }}
                    >
                      <label>Keyword</label>
                    </Typography>
                    <TextField
                      label=""
                      placeholder="Enter keywords..."
                      name="keywords"
                      variant="outlined"
                      fullWidth
                      value={values?.keywords}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched?.keywords && Boolean(errors?.keywords)}
                      helperText={<>{touched?.keywords && errors?.keywords}</>}
                    />
                  </Grid>

                  {showPrograms && (
                    <Grid size={{ md: 5, sm: 5, xs: 12 }}>
                      <Typography
                        variant="h6"
                        component="h6"
                        className="filterFormLabel"
                        style={{ fontSize: '14px', fontWeight: 'bold' }}
                      >
                        <label>Programs</label>
                      </Typography>
                      <FormControl variant="outlined" fullWidth className={``}>
                        <Select
                          fullWidth
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          label=""
                          defaultValue={
                            values?.policy_shortname
                              ? values?.policy_shortname
                              : ''
                          }
                          name="policy_shortname"
                          variant="outlined"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched?.policy_shortname &&
                            Boolean(errors?.policy_shortname)
                          }
                          displayEmpty
                        >
                          <MenuItem value="">Select</MenuItem>
                          {programPolicies.map((item) => (
                            <MenuItem
                              key={item.shortname}
                              value={item.shortname}
                            >
                              {item.title}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  <Grid size={{ md: 4, sm: 4, xs: 12 }}>
                    <Typography
                      variant="h6"
                      component="h6"
                      className="filterFormLabel"
                      style={{ fontSize: '14px', fontWeight: 'bold' }}
                    >
                      <label>Procedure Code</label>
                    </Typography>
                    <TextField
                      label=""
                      placeholder="Enter procedure code..."
                      name="procedure_code"
                      variant="outlined"
                      fullWidth
                      value={values?.procedure_code}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched?.procedure_code &&
                        Boolean(errors?.procedure_code)
                      }
                      helperText={
                        <>{touched?.procedure_code && errors?.procedure_code}</>
                      }
                    />
                  </Grid>

                  <Grid size={{ md: 3, sm: 3, xs: 12 }}>
                    <Typography
                      variant="h6"
                      component="h6"
                      className="filterFormLabel"
                      style={{ fontSize: '14px', fontWeight: 'bold' }}
                    >
                      <label>Policy Status</label>
                    </Typography>
                    <FormGroup
                      row
                      style={{ alignItems: 'center', display: 'flex' }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={handleChange}
                            value="Published"
                            name="status"
                            checked={values.status.includes('Published')}
                          />
                        }
                        label="Final"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={handleChange}
                            value="Review"
                            name="status"
                            checked={values.status.includes('Review')}
                          />
                        }
                        label="Draft"
                      />
                    </FormGroup>
                  </Grid>

                  {showLOB && (
                    <Grid size={{ md: 5, sm: 5, xs: 12 }}>
                      <Typography
                        variant="h6"
                        component="h6"
                        className="filterFormLabel"
                        style={{ fontSize: '14px', fontWeight: 'bold' }}
                      >
                        <label>Line of Business</label>
                      </Typography>

                      <FormControl variant="outlined" fullWidth className={``}>
                        <Autocomplete
                          // name="lob"
                          multiple
                          options={lobOptions.filter(
                            (option: any) => !selectedLobs.includes(option)
                          )}
                          getOptionLabel={(option) => option}
                          value={selectedLobs}
                          // label="Select LOB"
                          onChange={(event, newValue) =>
                            setSelectedLobs(newValue)
                          }
                          renderTags={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => (
                              <Chip
                                {...getTagProps({ index })}
                                key={option}
                                label={option}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              placeholder="Select a LOB..."
                              {...params}
                              label=""
                              variant="outlined"
                            />
                          )}
                        />
                        <FormHelperText
                          className={
                            errors.lob && touched.lob && errors.lob
                              ? 'Mui-error'
                              : ''
                          }
                        >
                          <>{errors.lob && touched.lob && errors.lob}</>
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  )}

                  <Grid
                    size={{ md: 5, sm: 5, xs: 12 }}
                    style={{ position: 'relative' }}
                  >
                    <Typography
                      variant="h6"
                      component="h6"
                      className="filterFormLabel"
                      style={{ fontSize: '14px', fontWeight: 'bold' }}
                    >
                      <label>Policy Date</label>
                    </Typography>

                    <DesktopDatePicker
                      label=""
                      format="MM/DD/YYYY"
                      value={
                        values?.service_date &&
                        dayjs(values?.service_date).isValid()
                          ? dayjs(values?.service_date)
                          : null
                      }
                      minDate={minDate}
                      maxDate={maxDate}
                      onChange={(value) => {
                        setFieldValue('service_date', value, true);
                        setTimeout(
                          () => setFieldTouched('service_date', true, true),
                          0
                        );
                      }}
                      slotProps={{
                        textField: {
                          error: Boolean(
                            touched?.service_date && errors?.service_date
                          ),
                          fullWidth: true,
                          helperText: (
                            <>{touched?.service_date && errors?.service_date}</>
                          ),
                          name: 'service_date',
                        },
                      }}
                    />
                    {values?.service_date && (
                      <Tooltip title="Remove Date">
                        <IconButton
                          className={`closeEndDatePosition`}
                          color="primary"
                          aria-label="Remove Date"
                          onClick={() => {
                            setFieldValue('service_date', null, true);
                            setFieldTouched('service_date', true);
                          }}
                        >
                          <ClearIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Grid>

                  <Grid size={12} gap={2} container justifyContent={'flex-end'}>
                    <ExportButton
                      disabled={!isValid}
                      loading={isExportLoading}
                      menuItems={[
                        { label: 'CSV', onClick: () => handleExport(values) },
                      ]}
                    />
                    <Button
                      color="primary"
                      variant="contained"
                      disabled={!isValid}
                      type="submit"
                    >
                      Submit
                    </Button>
                  </Grid>
                </Grid>
              </form>
            );
          }}
        </Formik>

        {/* Loader Overlay */}
        <PageLoader openLoader={openLoader} isGlobal={true} />
      </Box>
    </LocalizationProvider>
  );
};

export default SearchForm;
